import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { PartnershipFormData } from "../../services/partnershipFormService";
import * as yup from "yup";
import { AppDispatch, RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { clearStatus, sendPartnershipForm } from "../../store/partnershipSlice";

const inputBaseSx = {
  "& .MuiOutlinedInput-root": {
    bgcolor: "white",
    borderRadius: 0,
    "& fieldset": {
      borderColor: "#e0e0e0",
      borderWidth: "1px",
      borderRadius: 0,
    },
    "&:hover fieldset": {
      borderColor: "#4285f4",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#4285f4",
    },
  },
  "& .MuiInputBase-input": {
    py: 1.5,
    fontSize: "16px",
    "&::placeholder": {
      color: "#adb5bd",
      opacity: 1,
      fontSize: "15px",
    },
  },
};

const schema: yup.ObjectSchema<PartnershipFormData> = yup.object({
  name: yup.string().trim().required("Name is required"),
  email: yup
    .string()
    .trim()
    .email("Invalid email")
    .required("Email is required"),
  company: yup.string().trim().required("Company is required"),
  partnershipType: yup.string().trim().required("Select partnership type"),
  message: yup
    .string()
    .trim()
    .min(10, "Please write at least 10 characters")
    .required("Message is required"),
});

export default function PartnershipForm() {
  const dispatch = useDispatch<AppDispatch>();
  const { isSubmitting, lastSubmitStatus } = useSelector(
    (s: RootState) => s.partnership
  );

  const {
    handleSubmit,
    register,
    reset,
    control,
    clearErrors,
    formState: { errors },
  } = useForm<PartnershipFormData>({
    mode: "onChange",
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      email: "",
      message: "",
      company: "",
      partnershipType: "",
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    try {
      await dispatch(sendPartnershipForm(values)).unwrap();
      reset();
    } catch (e) {
      console.error(
        "PartnershipForm submit error:",
        e instanceof Error ? e.message : e
      );
    }
  });

  const handleCloseAlert = () => dispatch(clearStatus());

  const handleFormBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      clearErrors();
    }
  };

  return (
    <Box
      component="form"
      onSubmit={onSubmit}
      onBlur={handleFormBlur}
      noValidate
      sx={{
        maxWidth: 598,
        mx: "auto",
        mt: { xs: 6, md: 10 },
      }}
    >
      <Typography
        variant="h3"
        fontWeight={800}
        sx={{
          mb: { xs: 2, md: 3 },
          color: "#1e293b",
          fontSize: { xs: "36px", md: "48px" },
          lineHeight: 1.2,
          textAlign: { xs: "center", md: "left" },
        }}
      >
        Let’s work together
      </Typography>
      <Typography
        sx={{
          mb: 2,
          color: "#414141",
          fontSize: "18px",
          lineHeight: "22.68 px",
          letterSpacing: "0.36 px",
          maxWidth: 578,
        }}
      >
        We'd love to hear from you. Fill out the form below and we'll get in
        touch shortly.
      </Typography>
      {lastSubmitStatus === "success" && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={handleCloseAlert}>
          Thank you for your message! We'll get back to you soon.
        </Alert>
      )}
      {lastSubmitStatus === "error" && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={handleCloseAlert}>
          Something went wrong. Please check your information and try again.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid
          size={12}
          sx={{
            mb: {
              md: 2,
            },
          }}
        >
          <Typography
            sx={{
              mb: 1,
              color: "#333",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            Select Partnership Type
          </Typography>
          <Controller
            name="partnershipType"
            control={control}
            render={({ field, fieldState: { error: selError } }) => (
              <FormControl fullWidth error={!!selError}>
                <Select
                  {...field}
                  displayEmpty
                  inputProps={{
                    "aria-label": field.value ? "Selected" : "Not selected",
                  }}
                  sx={{
                    bgcolor: "white",
                    borderRadius: 0,
                    border: "none",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#e0e0e0",
                      borderWidth: "1px",
                      borderRadius: 0,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4285f4",
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#4285f4",
                    },
                    "& .MuiInputBase-input": {
                      py: 1.5,
                      fontSize: "15px",
                    },
                  }}
                >
                  <MenuItem value="">
                    <em>Not selected</em>
                  </MenuItem>
                  <MenuItem value="technologyPartners">
                    Technology Partners
                  </MenuItem>
                  <MenuItem value="implementationPartners">
                    Implementation Partners
                  </MenuItem>
                  <MenuItem value="investors">Investors</MenuItem>
                  <MenuItem value="systemIntegrators">
                    System Integrators
                  </MenuItem>
                </Select>
                {selError && (
                  <FormHelperText>{selError.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Grid>
        <Grid
          size={{ xs: 12, md: 6 }}
          sx={{
            mb: {
              md: 2,
            },
          }}
        >
          <Typography
            sx={{
              mb: 1,
              color: "#333",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            Your Name
          </Typography>
          <TextField
            placeholder="Please, write your name"
            fullWidth
            {...register("name")}
            error={!!errors.name}
            helperText={errors.name?.message}
            sx={inputBaseSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography
            sx={{
              mb: 1,
              color: "#333",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            Your E-mail
          </Typography>
          <TextField
            type="email"
            placeholder="Please, write your E-mail"
            fullWidth
            {...register("email")}
            error={!!errors.email}
            helperText={errors.email?.message}
            sx={inputBaseSx}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Typography
            sx={{
              mb: 1,
              color: "#333",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            Your Company Name
          </Typography>
          <TextField
            placeholder="Please, write your company name"
            fullWidth
            {...register("company")}
            error={!!errors.company}
            helperText={errors.company?.message}
            sx={inputBaseSx}
          />
        </Grid>
        <Grid
          size={12}
          sx={{
            mt: {
              md: 2,
            },
          }}
        >
          <Typography
            sx={{
              mb: 1,
              color: "#333",
              fontWeight: 600,
              fontSize: "16px",
            }}
          >
            Your Message
          </Typography>
          <TextField
            placeholder="We are eager to hear your questions!"
            fullWidth
            multiline
            rows={6}
            {...register("message")}
            error={!!errors.message}
            helperText={errors.message?.message}
            sx={inputBaseSx}
          />
        </Grid>
        <Grid size={12}>
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                bgcolor: "#5C78FF",
                borderRadius: "30px",
                px: 4,
                py: 1.5,
                fontSize: "16px",
                fontWeight: 600,
                textTransform: "none",
                boxShadow: "0 4px 15px rgba(92, 120, 255, 0.3)",
                "&:hover": {
                  bgcolor: "#4660c8",
                  boxShadow: "0 6px 20px rgba(92, 120, 255, 0.4)",
                },
                "&:disabled": {
                  bgcolor: "#ccc",
                },
              }}
            >
              {isSubmitting ? "Sending..." : "Submit Request"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
